import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import { Send, Paperclip, X, FileText } from 'lucide-react';
import { toast } from 'sonner';

const ChatInput = ({ onSend, loading }) => {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const [files, setFiles] = useState([]);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);

  const handleFileChange = (e) => {
    const selected = Array.from(e.target.files || []);
    const allowed = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];
    const valid = selected.filter(f => {
      if (!allowed.includes(f.type)) {
        toast.error(`${t('common.error')}: ${f.name}`);
        return false;
      }
      if (f.size > 10 * 1024 * 1024) {
        toast.error(`${t('common.error')}: ${f.name} (max 10MB)`);
        return false;
      }
      return true;
    });
    setFiles(prev => [...prev, ...valid]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeFile = (idx) => {
    setFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = () => {
    if (loading) return;
    if (!text.trim() && files.length === 0) return;
    onSend(text, files);
    setText('');
    setFiles([]);
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleInput = (e) => {
    setText(e.target.value);
    const el = textareaRef.current;
    if (el) {
      el.style.height = 'auto';
      el.style.height = Math.min(el.scrollHeight, 200) + 'px';
    }
  };

  return (
    <div className="border-t border-slate-200 bg-white px-4 lg:px-6 py-4">
      <div className="max-w-3xl mx-auto">
        {files.length > 0 && (
          <div className="mb-2 flex flex-wrap gap-2">
            {files.map((f, i) => (
              <div key={`${f.name}-${f.size}-${i}`} className="inline-flex items-center gap-2 bg-blue-50 border border-blue-100 rounded-lg px-2.5 py-1.5 text-xs">
                <FileText className="w-3.5 h-3.5 text-blue-700" />
                <span className="text-blue-900 font-medium truncate max-w-[180px]">{f.name}</span>
                <span className="text-blue-600">{(f.size / 1024).toFixed(0)}KB</span>
                <button
                  onClick={() => removeFile(i)}
                  className="text-blue-600 hover:text-red-600 transition-colors"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="relative rounded-2xl border border-slate-300 bg-white shadow-sm focus-within:border-blue-500 focus-within:ring-4 focus-within:ring-blue-500/10 transition-all">
          <textarea
            ref={textareaRef}
            value={text}
            onChange={handleInput}
            onKeyDown={handleKeyDown}
            placeholder={t('chat.placeholder')}
            rows={1}
            disabled={loading}
            className="w-full resize-none bg-transparent pl-4 pr-24 py-3.5 text-[15px] text-slate-900 placeholder:text-slate-400 focus:outline-none disabled:opacity-60"
            style={{ maxHeight: '200px' }}
          />

          <div className="absolute right-2 bottom-2 flex items-center gap-1">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf,.png,.jpg,.jpeg"
              onChange={handleFileChange}
              className="hidden"
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => fileInputRef.current?.click()}
              disabled={loading}
              className="w-9 h-9 text-slate-500 hover:text-blue-700 hover:bg-blue-50"
              title={t('common.upload')}
            >
              <Paperclip className="w-4 h-4" />
            </Button>
            <Button
              type="button"
              size="icon"
              onClick={handleSubmit}
              disabled={loading || (!text.trim() && files.length === 0)}
              className="w-9 h-9 bg-blue-700 hover:bg-blue-800 disabled:bg-slate-200 disabled:text-slate-400"
              title={t('chat.send')}
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <div className="mt-2 text-center text-[11px] text-slate-400">
          HC Assistant — {t('login.platformSubtitle')}
        </div>
      </div>
    </div>
  );
};

export default ChatInput;
